import React, { useMemo, useState } from "react";
import {
  Bookmark,
  LockKeyhole,
  MapPin,
  MoreHorizontal,
} from "lucide-react";
import { Switch } from "@exiftrator/shared/components/ui";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@exiftrator/shared/components/ui";
import { CATEGORY_METADATA } from "../../config/fieldDefinitions";
import {
  OVERFLOW_MENU_CHECKABLE_ITEM_CLASS,
  OVERFLOW_MENU_CONTENT_CLASS,
  OVERFLOW_MENU_ITEM_CLASS,
  OVERFLOW_MENU_LABEL_CLASS,
  OVERFLOW_MENU_SEPARATOR_CLASS,
  OVERFLOW_MENU_SHORTCUT_CLASS,
} from "../Header/overflowMenuStyles";
import {
  HeaderActionButton,
  HeaderActionGroup,
  HeaderIconButton,
} from "../Header/HeaderActions";
import { PanelHeader } from "../PanelHeader";
import { WORKSPACE_VIEW_META } from "../Workspace/workspaceViewMeta";
import { ViewIndicator } from "../ViewIndicator";
import { cn } from "../utils/cn";
import { METADATA_ACTION_DIALOG_COPY } from "../common/metadataActionDialogCopy";

/**
 * TableToolbar - Modern command bar for table view
 *
 * Design principles:
 * - Progressive disclosure: show what's needed for current context
 * - Visual hierarchy: primary actions prominent, secondary hidden
 * - Mode-aware: different layouts for default vs bulk edit vs selection states
 */

export function TableToolbar({
  bulkEditMode = false,
  selectedCellCount = 0,
  selectedRowCount = 0,
  onToggleBulkEdit,
  onOpenLocation,
  onOpenPreset,
  onRestoreOriginalMetadata,
  onClearAllMetadata,
  metadataActionsDisabled = false,
  onToggleColumnVisibility,
  onOpenShortcuts,
  allFields = [],
  canCustomizeFields = true,
  listControls = null,
}) {
  const tableViewMeta = WORKSPACE_VIEW_META.table;
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const fieldsByCategory = useMemo(() => {
    const groups = {};
    allFields.forEach((field) => {
      const category = field.category || "other";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(field);
    });
    return groups;
  }, [allFields]);

  const hasCellSelection = selectedCellCount > 0;
  const hasRowSelection = selectedRowCount > 0;

  return (
    <PanelHeader
      tone="contrast"
      title=""
      className={`transition-all duration-200 ${bulkEditMode ? "border-accent-primary/20" : "border-border-default"}`}
      leftContent={
        <ViewIndicator
          label={tableViewMeta.label}
          icon={tableViewMeta.icon}
        />
      }
      rightContent={
        <div className="flex items-center gap-1">
          {listControls}
          <HeaderActionGroup className="gap-1">
            <HeaderActionButton
              intent="secondary"
              onClick={onOpenLocation}
              title="Location (⌘⇧L, fallback ⌘⇧G)"
            >
              <MapPin data-icon="inline-start" className="size-4" strokeWidth={2.25} />
              Location
            </HeaderActionButton>
            <HeaderActionButton
              intent="secondary"
              onClick={onOpenPreset}
              title="Presets (⌘⇧P)"
            >
              <Bookmark data-icon="inline-start" className="size-4" strokeWidth={2.25} />
              Presets
            </HeaderActionButton>
          </HeaderActionGroup>

          <div
            className={cn(
              "ml-1 h-9 px-3 rounded-md inline-flex items-center gap-2",
              bulkEditMode || hasCellSelection || hasRowSelection
                ? "bg-accent-primary/10 text-accent-primary"
                : "text-text-secondary",
            )}
            title="Toggle bulk edit mode"
          >
            <Switch
              checked={bulkEditMode}
              onCheckedChange={() => onToggleBulkEdit?.()}
              aria-label="Toggle bulk edit mode"
            />
            <span className="text-[13px] font-medium">Bulk Edit</span>
          </div>

          <DropdownMenu open={columnMenuOpen} onOpenChange={setColumnMenuOpen}>
            <DropdownMenuTrigger asChild>
              <HeaderIconButton
                intent="utility"
                aria-label="Table options"
                title="Table options"
              >
                <MoreHorizontal data-icon="inline-start" className="size-4" strokeWidth={2.5} />
              </HeaderIconButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className={cn(OVERFLOW_MENU_CONTENT_CLASS, "w-[240px]")}
            >
              {!canCustomizeFields && (
                <>
                  <DropdownMenuItem
                    disabled
                    className={cn(
                      OVERFLOW_MENU_ITEM_CLASS,
                      "text-[13px] text-text-supporting",
                    )}
                  >
                    <LockKeyhole className="size-4 mr-2" />
                    Column visibility requires Pro
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={OVERFLOW_MENU_SEPARATOR_CLASS} />
                </>
              )}
              {Object.entries(fieldsByCategory).map(([categoryId, fields]) => {
                const categoryMeta = CATEGORY_METADATA[categoryId];
                if (!categoryMeta) return null;

                return (
                  <DropdownMenuGroup key={categoryId}>
                    <DropdownMenuLabel
                      className={cn(
                        OVERFLOW_MENU_LABEL_CLASS,
                        "text-[10px] uppercase tracking-[0.08em]",
                      )}
                    >
                      {categoryMeta.label}
                    </DropdownMenuLabel>
                    {fields.map((field) => (
                      <DropdownMenuCheckboxItem
                        key={field.id}
                        checked={field.enabled}
                        disabled={!canCustomizeFields}
                        onCheckedChange={() =>
                          onToggleColumnVisibility?.(field.id)
                        }
                        onSelect={(e) => e.preventDefault()}
                        className={OVERFLOW_MENU_CHECKABLE_ITEM_CLASS}
                      >
                        <span className="text-[13px] leading-none">{field.label}</span>
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuGroup>
                );
              })}

              <DropdownMenuSeparator className={OVERFLOW_MENU_SEPARATOR_CLASS} />

              <DropdownMenuGroup>
                <DropdownMenuLabel
                  className={cn(
                    OVERFLOW_MENU_LABEL_CLASS,
                    "text-[10px] uppercase tracking-[0.08em]",
                  )}
                >
                  Metadata Actions
                </DropdownMenuLabel>
                <DropdownMenuItem
                  disabled={metadataActionsDisabled || !onRestoreOriginalMetadata}
                  onClick={onRestoreOriginalMetadata}
                  className={OVERFLOW_MENU_ITEM_CLASS}
                >
                  {METADATA_ACTION_DIALOG_COPY.restore.actionLabel}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={metadataActionsDisabled || !onClearAllMetadata}
                  onClick={onClearAllMetadata}
                  className={OVERFLOW_MENU_ITEM_CLASS}
                >
                  {METADATA_ACTION_DIALOG_COPY.clear.actionLabel}
                </DropdownMenuItem>
              </DropdownMenuGroup>

              <DropdownMenuSeparator className={OVERFLOW_MENU_SEPARATOR_CLASS} />

              <DropdownMenuItem
                onClick={onOpenShortcuts}
                className={OVERFLOW_MENU_ITEM_CLASS}
              >
                <span>Keyboard shortcuts</span>
                <DropdownMenuShortcut className={OVERFLOW_MENU_SHORTCUT_CLASS}>
                  ?
                </DropdownMenuShortcut>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
    />
  );
}
